import "core-js/modules/es.array.flat";
import "core-js/modules/es.array.unscopables.flat";
import "core-js/modules/es.string.includes";
import "core-js/modules/es.string.replace";
import "core-js/modules/es.string.split";
import "core-js/modules/es.string.trim";
import "core-js/modules/web.dom-collections.iterator";
import "core-js/modules/es.array.flat";
import "core-js/modules/es.array.unscopables.flat";
import "core-js/modules/es.string.includes";
import "core-js/modules/es.string.replace";
import "core-js/modules/es.string.split";
import "core-js/modules/es.string.trim";
import "core-js/modules/web.dom-collections.iterator";
/**
 * Section: Recommended Products
 * ------------------------------------------------------------------------------
 * A section for fetching and displaying recommended products.
 * Uses the Shopify recommended products Ajax endpoint.
 */

import { formatMoney } from '@shopify/theme-currency';
import { register } from '@shopify/theme-sections';
import { values as breakpoints } from '~/helpers/breakpoints';
import Carousel from '~/components/carousel';
import cssClasses from '~/helpers/cssClasses';
/**
 * DOM selectors.
 */

const selectors = {
  body: '[js-recommended-prods="body"]',
  cardGrid: '[js-recommended-prods="cardGrid"]',
  carousel: '[js-recommended-prods="carousel"]',
  container: '[js-recommended-prods="container"]',
  drag: '[js-recommended-prods="drag"]',
  footerRow: '[js-recommended-prods="footerRow"]',
  item: '[js-recommended-prods="item"]',
  nextButton: '[js-recommended-prods="next"]',
  prevButton: '[js-recommended-prods="prev"]',
  productLink: '[js-product-link]',
  productPrice: '[js-recommended-products="productPrices"]',
  productTitle: '[js-product-link="product-title"]',
  responsiveImage: '[js-responsive-image]'
};
/**
 * Register the `recommended-prods` section type.
 */

register('recommended-prods', {
  /**
   * Initialize section.
   */
  init() {
    this.cacheSelectors();
  },

  /**
   * Cache selectors.
   * - Define necessary variables and make available for component use.
   */
  cacheSelectors() {
    const body = document.querySelector("[data-section-id=\"".concat(this.id, "\"] ").concat(selectors.body));
    const cardGrid = document.querySelector("[data-section-id=\"".concat(this.id, "\"] ").concat(selectors.cardGrid));
    const carousel = document.querySelector("[data-section-id=\"".concat(this.id, "\"] ").concat(selectors.carousel));
    const container = document.querySelector(selectors.container);
    const item = document.querySelector("[data-section-id=\"".concat(this.id, "\"] ").concat(selectors.item));
    const nextButton = document.querySelector("[data-section-id=\"".concat(this.id, "\"] ").concat(selectors.nextButton));
    const prevButton = document.querySelector("[data-section-id=\"".concat(this.id, "\"] ").concat(selectors.prevButton));
    const section = document.querySelector("[data-section-id=\"".concat(this.id, "\"]"));
    this.cache = {
      body,
      cardGrid,
      carousel,
      container,
      item,
      nextButton,
      prevButton,
      section
    };
  },

  /**
   * Fetch recommended products data from Shopify Recommended Products API.
   * @param {Number} productsIds - Ids of products to base recommended products on.
   */
  fetchRecommendedProducts(productsIds) {
    const productsIdsArray = productsIds.split(',').map(id => id.trim());
    const productRecLinks = productsIdsArray.map(productId => "/recommendations/products.json?product_id=".concat(productId));
    const productRecFetches = productRecLinks.map(productRecLink => fetch(productRecLink));
    Promise.all(productRecFetches).then(fetchesData => Promise.all(fetchesData.map(fetchData => fetchData.json()))).then(arrays => {
      return arrays.map(array => array.products).flat();
    }).then(products => {
      const prodsIds = new Set(products.map(product => product.id));
      return [...prodsIds].map(productId => products.find(product => product.id === productId));
    }).then(products => {
      if (products && products.length > 0) {
        this.renderRecommendedProducts(products);
        this.constructCarousel();
        return;
      }

      throw new Error('No recommended products to display');
    }).then(() => {
      window.setWishAddCollEventListeners();
      window.iwishCheckColl();
    }).catch(error => {
      this.container.remove();
      window.console.error(error);
    });
  },

  /**
   * Render product cards from returned recommendation data.
   * - Creates a DocumentFragment to store generated HTML.
   * - productCardTemplate node is duplicated as template and product data added.
   * - fragment appended to templateContainer to add in DOM.
   * - Class added to templateContainer to show in DOM once elements appended.
   * @param {Object} products - Recommended products api response products array.
   */
  renderRecommendedProducts(products) {
    const templateContainer = this.cache.cardGrid;
    const productCardTemplate = this.cache.item;
    const fragment = document.createDocumentFragment();
    products.forEach(product => {
      const template = productCardTemplate.cloneNode(true);
      const renderObjects = {
        product,
        template
      };
      this.updateProductObject(renderObjects);
      fragment.appendChild(template);
    });
    productCardTemplate.parentNode.removeChild(productCardTemplate);
    templateContainer.appendChild(fragment);
    this.cache.section.classList.add(cssClasses.added);
  },

  /**
   * Update product pages and links.
   * @param {Object} renderObjects - Object containing product data and template nodeList.
   */
  updateProductObject(renderObjects) {
    this.createProductBadges(renderObjects);
    this.createProductWishlistButton(renderObjects);
    this.updateProductImages(renderObjects);
    this.updateProductLinks(renderObjects);
    this.updateProductPrice(renderObjects);
    this.updateProductTitle(renderObjects);
    this.updateYotpoStars(renderObjects);
    this.refreshYotpo();
  },

  /**
   * Create product badge HTML.
   * - Loops through product tags and filters out tags containing 'badge:'.
   * - For all tags containing 'badge:' runs renderBadgeTemplate.
   * @param {Object} renderObjects - Object containing product data and template nodeList.
   * @param {Object} renderObjects.product - Product data returned from Recommended Products API.
   * @param {NodeList} renderObjects.template - DOM Node of Product Card where product data is being added.
   */
  createProductBadges(renderObjects) {
    const product = renderObjects.product;
    const template = renderObjects.template;
    const soldOut = !product.available;
    const onSale = product.compare_at_price > product.price;
    const badgeNode = template.querySelector('span.product-card-template__badge');

    if (soldOut) {
      badgeNode.classList.add('product-card-template__badge--brown');
      badgeNode.innerHTML = template.dataset.soldOut;
    } else if (onSale) {
      badgeNode.classList.add('product-card-template__badge--orange');
      badgeNode.innerHTML = template.dataset.onSale;
    } else {
      const firstTag = product.tags.find(tag => tag.includes('pfs:label-'));

      if (firstTag) {
        const preparedTag = firstTag.split('pfs:label-');
        badgeNode.innerHTML = preparedTag;
      } else {
        badgeNode.classList.add('hide_badge');
      }
    }
  },

  /**
   * Create product badge HTML.
   * - Loops through product tags and filters out tags containing 'badge:'.
   * - For all tags containing 'badge:' runs renderBadgeTemplate.
   * @param {Object} renderObjects - Object containing product data and template nodeList.
   * @param {Object} renderObjects.product - Product data returned from Recommended Products API.
   * @param {NodeList} renderObjects.template - DOM Node of Product Card where product data is being added.
   */
  createProductWishlistButton(renderObjects) {
    var _product$variants$;

    const product = renderObjects.product;
    const template = renderObjects.template;
    const wishlistButtonNode = template.querySelector('button.product-card-template__heart');
    const firestVariantId = product === null || product === void 0 ? void 0 : (_product$variants$ = product.variants[0]) === null || _product$variants$ === void 0 ? void 0 : _product$variants$.id;
    wishlistButtonNode.classList.add("iwishcheck");
    wishlistButtonNode.setAttribute("data-variant", firestVariantId);
    wishlistButtonNode.setAttribute("data-product", product.id);
    wishlistButtonNode.setAttribute("data-pTitle", product.title);
  },

  /**
   * Return a div containing tag data.
   * @param {String} tag - Product tag.
   * @returns {String}
   */
  renderBadgeTemplate(tag) {
    return "\n      <div class=\"card__badge badge badge--secondary\">\n        <span>".concat(tag.replace('badge:', ''), "</span>\n      </div>\n    ");
  },

  /**
   * Update product links in HTML template.
   * - Sets selectors.productLink items to renderObjects.product.url.
   * @param {Object} renderObjects - Object containing product object and template nodeList.
   * @param {Object} renderObjects.product - Product data returned from Recommended Products API.
   * @param {NodeList} renderObjects.template - DOM Node of Product Card where product data is being added.
   */
  updateProductLinks(renderObjects) {
    const links = renderObjects.template.querySelectorAll(selectors.productLink);
    [...links].forEach(link => {
      link.href = renderObjects.product.url;
    });
  },

  /**
   * Update product title in HTML template.
   * - Sets selectors.productTitle items to renderObjects.product.title.
   * @param {Object} renderObjects - Object containing product object and template nodeList.
   * @param {Object} renderObjects.product - Product data returned from Recommended Products API.
   * @param {NodeList} renderObjects.template - DOM Node of Product Card where product data is being added.
   */
  updateProductTitle(renderObjects) {
    const itemTitleRaw = renderObjects.product.title.split('|');
    let itemTitle = renderObjects.product.title;

    if (itemTitleRaw.length > 0) {
      itemTitle = itemTitleRaw[0];
    }

    renderObjects.template.querySelector(selectors.productTitle).innerText = itemTitle;
  },

  /**
   * Update Yotpo star value for each card.
   * @param {Object} renderObjects - Object containing product object and template nodeList.
   */
  updateYotpoStars(renderObjects) {
    const footerRow = renderObjects.template.querySelector(selectors.footerRow);
    const yotpoStars = "\n      <div\n        class=\"yotpo bottomLine\"\n        data-yotpo-product-id=\"".concat(renderObjects.product.id, "\"\n      >\n      </div>\n    ");
    footerRow.insertAdjacentHTML('beforeend', yotpoStars);
  },

  /**
   * Refresh Yotpo widgets after content is loaded.
   */
  refreshYotpo() {
    // eslint-disable-next-line no-undef
    Yotpo.ready(() => {
      // eslint-disable-next-line no-undef
      yotpo.initWidgets();
    });
  },

  /**
   * Update product images in HTML template.
   * - Sets correct lazyload classes on the image elements.
   * - Set first image element to be featured_image if property exists.
   * - Loops through remaining product.image and imageElements to show additional images.
   * - Runs generateImageUrl to generate appropriate image urls.
   * @param {Object} renderObjects - Object containing product object and template nodeList.
   * @param {Object} renderObjects.product - Product data returned from Recommended Products API.
   * @param {NodeList} renderObjects.template - DOM Node of Product Card where product data is being added.
   */
  updateProductImages(renderObjects) {
    const imageElements = renderObjects.template.querySelectorAll("".concat(selectors.responsiveImage, " img"));
    [...imageElements].forEach((el, index) => {
      let image = renderObjects.product.images[0];
      el.classList.remove(cssClasses.lazyloaded);
      el.classList.add(cssClasses.lazyload);

      if (index === 0 && renderObjects.product.featured_image) {
        image = renderObjects.product.featured_image;
      } else if (renderObjects.product.images[index]) {
        image = renderObjects.product.images[index];
      }

      el.src = this.generateImageUrl(image, '3x');
      el.dataset.src = this.generateImageUrl(image, 'responsive');
    });
  },

  /**
   * Modifies image URL string to add size parameter or LazySizes responsive markup.
   * @param {String} image - Product Image URL.
   * @param {String} size - Either "responsive" or size string.
   * @returns {String}
   */
  generateImageUrl(image, size) {
    const imageSize = size === 'responsive' ? '{width}x' : size;

    if (image) {
      return image.replace('.jpg', "_".concat(imageSize, ".jpg"));
    }

    return false;
  },

  /**
  * Update product price in HTML template.
  * - Finds selectors.productPrice elements in template node.
  * - Renders new product price into elements using renderProductPrice.
  * @param {Object} renderObjects - Object containing product object and template nodeList to get price from.
  * @param {object} renderObjects.product - Product data returned from Recommended Products API.
  * @param {NodeList} renderObjects.template - DOM Node of Product Card where product data is being added.
  */
  updateProductPrice(renderObjects) {
    renderObjects.template.querySelector(selectors.productPrice).innerHTML = this.renderProductPrice(renderObjects.product);
  },

  /**
   * Get variant price template.
   * - Checks if it's discounted or not.
   * @param {Object} product - Product object to get price from.
   * @returns {String}
   */
  renderProductPrice(product) {
    let priceTemplate = '';

    if (product.compare_at_price > product.price) {
      if (product.price_varies) {
        priceTemplate += "\n          <span class=\"price h5\" data-price=\"".concat(product.price, "\">\n            ").concat(formatMoney(product.price, theme.moneyFormat), "\n          </span>");
      } else {
        priceTemplate += "\n          <span class=\"price h5\" data-price=\"".concat(product.price, "\">\n            ").concat(formatMoney(product.price, theme.moneyFormat), "\n          </span>\n        ");
      }
    } else if (product.price_varies) {
      priceTemplate += "\n        ".concat(theme.strings.productPrice.fromText, "\n        <span class=\"price h5\" data-price=\"").concat(product.price, "\">\n          ").concat(formatMoney(product.price, theme.moneyFormat), "\n        </span>\n      ");
    } else {
      priceTemplate += "\n        <span class=\"price h5\" data-price=\"".concat(product.price, "\">\n          ").concat(formatMoney(product.price, theme.moneyFormat), "\n        </span>\n      ");
    }

    if (product.compare_at_price > product.price) {
      priceTemplate += "\n        <span class=\"visually-hidden\">\n          ".concat(theme.strings.productPrice.regularPrice, "\n        </span>\n        <s class=\"compare_at_price body-1\" data-price=\"").concat(product.compare_at_price, "\">\n          ").concat(formatMoney(product.compare_at_price, theme.moneyFormat), "\n        </s>\n      ");
    }

    return priceTemplate;
  },

  /**
   * Get total number of product cards.
   * @returns {Number}
   */
  getProductCardCount() {
    return document.querySelectorAll(selectors.item).length;
  },

  /**
   * Construct carousel using imported Carousel component.
   */
  constructCarousel() {
    const carousel = Carousel(selectors.carousel, {
      swiper: {
        initialSlide: 0,
        loop: true,
        breakpoints: {
          [breakpoints.tiny]: {
            centeredSlides: false,
            slidesPerView: 'auto',
            slidesOffsetBefore: 16,
            spaceBetween: 20
          },
          [breakpoints.medium]: {
            loopedSlides: 1,
            loopAdditionalSlides: 3,
            slidesPerView: 'auto',
            spaceBetween: 40
          }
        },
        navigation: {
          nextEl: this.cache.nextButton,
          prevEl: this.cache.prevButton
        }
      }
    });
    carousel.init();
  },

  /**
   * Detect smartphones and touchscreens.
   * @returns {Boolean} - returns true if device is smartphones or touchscreens.
   */
  isMobile() {
    return window.matchMedia('(hover: none) and (pointer: coarse)').matches;
  },

  /**
   * Callback function when section is loaded via `sections.load()` or by
   * the Theme Editor `shopify:section:load` event.
   */
  onLoad() {
    this.init();
    this.fetchRecommendedProducts(this.cache.section.dataset.productsIds);
  }

});